import axios from 'axios';
import { getToken, clearSession, setLogoutReason } from './authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE,
  // Timeout verhindert, dass ein hängender Request (z.B. langsames Mobilnetz
  // oder Proxy ohne Antwort) seinen Concurrency-Slot dauerhaft blockiert und
  // damit den globalen Limiter zum Stillstand bringt ("Speichern" hängt ewig).
  timeout: 45000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// Globaler Concurrency-Limiter: Beim Laden mancher Seiten (z. B. /massnahmen)
// feuern mehrere Hooks gleichzeitig viele Requests ab. Der Ingress/Proxy
// quittiert solche Bursts mit 429. Wir entzerren die Last, indem nie mehr als
// MAX_CONCURRENT Requests gleichzeitig „in flight" sind; der Rest wartet in
// einer FIFO-Queue. Zusammen mit dem 429-Retry unten verhindert das den Storm.
// ---------------------------------------------------------------------------
const MAX_CONCURRENT = 5;
let activeRequests = 0;
const waitQueue = [];

const acquireSlot = () =>
  new Promise((resolve) => {
    if (activeRequests < MAX_CONCURRENT) {
      activeRequests += 1;
      resolve();
    } else {
      waitQueue.push(resolve);
    }
  });

const releaseSlot = () => {
  const next = waitQueue.shift();
  if (next) {
    next();
  } else {
    activeRequests = Math.max(0, activeRequests - 1);
  }
};

// Add auth token to requests (+ Slot reservieren)
api.interceptors.request.use(async (config) => {
  await acquireSlot();
  config.__slotAcquired = true;
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Device-Token aus localStorage haengen wir an JEDE Anfrage; das Backend
  // nutzt es nur dort, wo es erwartet wird (TV/Terminal-Endpoints).
  const deviceToken = localStorage.getItem('device_token');
  if (deviceToken) {
    config.headers['X-Device-Token'] = deviceToken;
  }
  return config;
});

// Handle 401 / 403 (Device) responses
api.interceptors.response.use(
  (response) => {
    if (response.config?.__slotAcquired) {
      response.config.__slotAcquired = false;
      releaseSlot();
    }
    return response;
  },
  async (error) => {
    const config = error.config;
    // Auth-/Login-Endpunkte: hier darf der globale 401-Redirect NICHT greifen,
    // sonst wird bei falschen Zugangsdaten die Login-Seite neu geladen und die
    // eigentliche Fehlermeldung verschwindet sofort. Die Login-Seite zeigt den
    // Fehler selbst an.
    const requestUrl = config?.url || '';
    const isAuthLoginEndpoint =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/register') ||
      requestUrl.includes('/webauthn/authenticate');
    // Slot freigeben, sobald die Anfrage abgeschlossen ist (auch bei Fehlern).
    if (config?.__slotAcquired) {
      config.__slotAcquired = false;
      releaseSlot();
    }
    // 429 (Rate Limit): sanftes Retry mit exponentiellem Back-off, statt den
    // Nutzer mit Fehler-Toasts zu überfluten. Bis zu 3 Versuche. Das erneute
    // api(config) durchläuft den Request-Interceptor und reserviert einen
    // neuen Slot. Ausnahme: Login-Endpunkte (Brute-Force-Lockout soll sofort
    // sichtbar werden, nicht 3x still wiederholt).
    if (error.response?.status === 429 && config && !isAuthLoginEndpoint) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount <= 3) {
        const retryAfter = parseFloat(error.response.headers?.['retry-after']);
        const delayMs = Number.isFinite(retryAfter)
          ? retryAfter * 1000
          : Math.min(2000, 400 * 2 ** (config.__retryCount - 1));
        await new Promise((r) => setTimeout(r, delayMs));
        return api(config);
      }
    }
    if (error.response?.status === 401 && !isAuthLoginEndpoint) {
      // Schutz vor Fehl-Abmeldungen: Ein einzelner transienter 401 (Netzwerk-
      // Race, kurzzeitiger Server-Hänger, einzelner Endpoint-Fehler) soll den
      // Nutzer NICHT sofort ausloggen — das wird sonst als „das andere Gerät
      // hat mich abgemeldet" wahrgenommen. Daher die Session einmal über
      // /auth/me gegenprüfen; nur wenn diese ebenfalls 401 liefert, ist die
      // Session wirklich ungültig → harter Logout.
      const isAuthMe = requestUrl.includes('/auth/me');
      if (!isAuthMe && !config.__sessionRechecked && getToken()) {
        config.__sessionRechecked = true;
        try {
          await api.get('/auth/me');
          // Session ist noch gültig → nur diese eine Anfrage abweisen,
          // KEIN Logout.
          return Promise.reject(error);
        } catch (recheckErr) {
          // /auth/me ebenfalls fehlgeschlagen → Session wirklich ungültig,
          // weiter zum harten Logout unten.
        }
      }
      clearSession();
      setLogoutReason('expired');
      window.location.href = '/login';
    }
    // 403 mit „Gerät nicht autorisiert" — Token ungueltig, lokal entfernen
    if (
      error.response?.status === 403 &&
      typeof error.response?.data?.detail === 'string' &&
      error.response.data.detail.toLowerCase().includes('gerät')
    ) {
      localStorage.removeItem('device_token');
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  getSessions: () => api.get('/auth/sessions'),
  revokeSession: (id) => api.delete(`/auth/sessions/${id}`),
  revokeOtherSessions: () => api.post('/auth/sessions/revoke-others'),
};

// Company Settings API
export const companyAPI = {
  get: () => api.get('/company'),
  update: (data) => api.put('/company', data),
  getOrgLabels: () => api.get('/company/org-labels'),
  updateOrgLabels: (data) => api.put('/company/org-labels', data),
  getApprovalChain: () => api.get('/company/approval-chain'),
  updateApprovalChain: (data) => api.put('/company/approval-chain', data),
  getOrgLevels: () => api.get('/org-structure/levels'),
  updateOrgLevels: (data) => api.put('/org-structure/levels', data),
  getOrgTree: () => api.get('/org-structure/tree'),
  createOrgUnit: (data) => api.post('/org-structure/units', data),
  updateOrgUnit: (id, data) => api.put(`/org-structure/units/${id}`, data),
  deleteOrgUnit: (id) => api.delete(`/org-structure/units/${id}`),
  setOrgUnitManagers: (id, data) => api.put(`/org-structure/units/${id}/managers`, data),
  getBranding: () => api.get('/company/branding'),
  listClosures: () => api.get('/company/closures'),
  createClosure: (data) => api.post('/company/closures', data),
  deleteClosure: (id) => api.delete(`/company/closures/${id}`),
};

// Organization Structure API
export const organizationAPI = {
  getStructure: () => api.get('/organization/structure'),
  managerCoverage: () => api.get('/organization/manager-coverage'),
};

// Branches API (Niederlassungen)
export const branchesAPI = {
  getAll: (params = {}) => api.get('/branches', { params }),
  getOne: (id) => api.get(`/branches/${id}`),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  delete: (id) => api.delete(`/branches/${id}`),
  duplicate: (id) => api.post(`/branches/${id}/duplicate`),
  importTemplate: () => api.get('/branches/import/template', { responseType: 'blob' }),
  exportCsv: (format = 'csv') => api.get(`/branches/export?format=${format}`, { responseType: 'blob' }),
  import: (file, dryRun = true) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/branches/import?dry_run=${dryRun}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Locations API (Standorte)
export const locationsAPI = {
  getAll: (params = {}) => api.get('/locations', { params }),
  getOne: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
  duplicate: (id) => api.post(`/locations/${id}/duplicate`),
  movePreview: (id) => api.get(`/locations/${id}/move-preview`),
  move: (id, targetBranchId) => api.post(`/locations/${id}/move`, { target_branch_id: targetBranchId }),
  importTemplate: () => api.get('/locations/import/template', { responseType: 'blob' }),
  exportCsv: (format = 'csv') => api.get(`/locations/export?format=${format}`, { responseType: 'blob' }),
  import: (file, dryRun = true) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/locations/import?dry_run=${dryRun}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Employees API
export const employeesAPI = {
  getAll: (params = {}) => api.get('/employees', { params }),
  getOne: (id) => api.get(`/employees/${id}`),
  getMyProfile: () => api.get('/employees/me'),  // Get current user's employee profile
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  patch: (id, partial) => api.patch(`/employees/${id}`, partial),
  hire: (id, data) => api.post(`/employees/${id}/hire`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  addTraining: (employeeId, training) => api.post(`/employees/${employeeId}/trainings`, training),
  deleteTraining: (employeeId, trainingId) => api.delete(`/employees/${employeeId}/trainings/${trainingId}`),
  linkUser: (employeeId, userId) => api.put(`/employees/${employeeId}/link-user`, null, { params: { user_id: userId } }),
  unlinkUser: (employeeId) => api.put(`/employees/${employeeId}/link-user`, null, { params: { user_id: null } }),
  getLinkedUser: (employeeId) => api.get(`/employees/${employeeId}/linked-user`),
  // System Access (NEW)
  createSystemAccess: (employeeId, data) => api.post(`/employees/${employeeId}/create-system-access`, data),
  removeSystemAccess: (employeeId) => api.delete(`/employees/${employeeId}/system-access`),
  // IT Access
  getITAccess: (employeeId) => api.get(`/employees/${employeeId}/it-access`),
  addITAccess: (employeeId, data) => api.post(`/employees/${employeeId}/it-access`, data),
  updateITAccess: (employeeId, accessId, data) => api.put(`/employees/${employeeId}/it-access/${accessId}`, data),
  deleteITAccess: (employeeId, accessId) => api.delete(`/employees/${employeeId}/it-access/${accessId}`),
  // Conversation/Meeting Notes
  listNotes: (employeeId, params = {}) => api.get(`/employees/${employeeId}/notes`, { params }),
  createNote: (employeeId, data) => api.post(`/employees/${employeeId}/notes`, data),
  updateNote: (employeeId, noteId, data) => api.put(`/employees/${employeeId}/notes/${noteId}`, data),
  deleteNote: (employeeId, noteId) => api.delete(`/employees/${employeeId}/notes/${noteId}`),
  listNoteFollowUps: (params = {}) => api.get('/employees/notes/follow-ups', { params }),
  addITAccessFromPreset: (employeeId, presetId) => api.post(`/employees/${employeeId}/it-access/from-preset/${presetId}`),
  // Get employees by role
  getByRole: (roleName) => api.get(`/employees-by-role`, { params: { role_name: roleName } }),
  // Duplikat-Bereinigung
  findDuplicates: () => api.get('/employees/duplicates'),
  mergeDuplicates: (keepId, removeIds) => api.post('/employees/duplicates/merge', { keep_id: keepId, remove_ids: removeIds }),
  // Monthly working hours
  getMonthlyHours: (employeeId, year) => api.get(`/employees/${employeeId}/monthly-hours`, { params: { year } }),
  setMonthlyHoursBulk: (employeeId, hoursList) => api.post(`/employees/${employeeId}/monthly-hours/bulk`, hoursList),
};

// IT Access Presets API
export const itAccessPresetsAPI = {
  getAll: () => api.get('/it-access/presets'),
  create: (data) => api.post('/it-access/presets', data),
  update: (presetId, data) => api.put(`/it-access/presets/${presetId}`, data),
  delete: (presetId) => api.delete(`/it-access/presets/${presetId}`),
};

// Import/Export API
export const importExportAPI = {
  getTemplate: () => api.get('/import/template', { responseType: 'blob' }),
  importEmployees: (file, dryRun = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/import/employees${dryRun ? '?dry_run=true' : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  exportEmployees: (branchId = null, format = 'xlsx', orgUnitId = null) => {
    const params = { format };
    if (orgUnitId && orgUnitId !== 'all') params.org_unit_id = orgUnitId;
    else if (branchId) params.branch_id = branchId;
    return api.get('/export/employees', { params, responseType: 'blob' });
  },
};

// Permissions API
export const permissionsAPI = {
  getAll: () => api.get('/permissions'),
  getOne: (userId) => api.get(`/permissions/${userId}`),
  update: (userId, data) => api.put(`/permissions/${userId}`, data),
};

// Role Definitions API (Berechtigungsrollen)
export const rolesAPI = {
  getAll: () => api.get('/role-definitions'),
  getOne: (roleId) => api.get(`/role-definitions/${roleId}`),
  create: (data) => api.post('/role-definitions', data),
  update: (roleId, data) => api.put(`/role-definitions/${roleId}`, data),
  delete: (roleId, { force = false } = {}) => api.delete(`/role-definitions/${roleId}`, { params: force ? { force: true } : {} }),
  cleanupDuplicates: () => api.post('/role-definitions/cleanup-duplicates'),
};

// Firmen-Dokumentvorlagen (für Workflow-Aufgaben "Dokument erforderlich")
export const documentTemplatesAPI = {
  list: () => api.get('/templates'),
};

// Users API (for admin)
export const usersAPI = {
  getAll: () => api.get('/users'),
  delete: (userId) => api.delete(`/users/${userId}`),
  cleanupOrphanPermissions: () => api.post('/users/cleanup-orphan-permissions'),
  updateRole: (userId, role) => api.put(`/users/${userId}/role`, { role }),
  resetPassword: (userId, newPassword) => api.put(`/users/${userId}/reset-password`, { new_password: newPassword }),
};

// Upload API
export const uploadAPI = {
  uploadCertificate: (file, meta = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (meta.employee_name) formData.append('employee_name', meta.employee_name);
    if (meta.document_title) formData.append('document_title', meta.document_title);
    if (meta.document_date) formData.append('document_date', meta.document_date);
    return api.post('/upload/certificate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Absences API (K, KK, O)
export const absencesAPI = {
  getAll: (params = {}) => api.get('/absences', { params }),
  getOne: (id) => api.get(`/absences/${id}`),
  create: (data) => api.post('/absences', data),
  update: (id, data) => api.put(`/absences/${id}`, data),
  delete: (id) => api.delete(`/absences/${id}`),
  getCalendar: (year, month) => api.get(`/absences/calendar/${year}/${month}`),
  getSummary: (year = null) => api.get('/absences/summary', { params: { year } }),
  getWeeklyPayrollReport: (startDate, endDate) =>
    api.get('/absences/payroll-report/weekly', { params: { start_date: startDate, end_date: endDate } }),
};

// Compliance API
export const complianceAPI = {
  getOverview: (params = {}) => api.get('/compliance/overview', { params }),
  exportHandlungsbedarf: (params = {}) =>
    api.get('/compliance/export', { params, responseType: 'blob' }),
  exportPoliceChecks: (params = {}) =>
    api.get('/compliance/export/police-checks', { params, responseType: 'blob' }),
  exportMeasles: (params = {}) =>
    api.get('/compliance/export/measles', { params, responseType: 'blob' }),
};

// Vacations API
export const vacationsAPI = {
  getAll: (params = {}) => api.get('/vacations', { params }),
  getPending: () => api.get('/vacations/pending'),
  create: (data) => api.post('/vacations', data),
  update: (id, data) => api.patch(`/vacations/${id}`, data),
  approve: (id, data) => api.put(`/vacations/${id}/approve`, data),
  delete: (id) => api.delete(`/vacations/${id}`),
  removeDays: (id, dates) => api.post(`/vacations/${id}/remove-days`, { dates }),
  getBalance: (employeeId, year = null) => api.get(`/vacations/balance/${employeeId}`, { params: { year } }),
  getAllBalances: (year = null) => api.get('/vacations/balance', { params: { year } }),
  preview: (startDate, endDate, employeeId = null) => api.get('/vacations/preview', { params: { start_date: startDate, end_date: endDate, employee_id: employeeId } }),
  getConflicts: (id) => api.get(`/vacations/${id}/conflicts`),
  getApprovalChain: (id) => api.get(`/vacations/${id}/approval-chain`),
  getEmployeeApprovalChain: (employeeId) => api.get(`/vacations/employee/${employeeId}/approval-chain`),
  setEmployeeApprovalChain: (employeeId, data) => api.put(`/vacations/employee/${employeeId}/approval-chain`, data),
};

// Shifts API
export const shiftsAPI = {
  getAll: (params = {}) => api.get('/shifts', { params }),
  create: (data) => api.post('/shifts', data),
  createSeries: (data) => api.post('/shifts/series', data),
  previewSeries: (data) => api.post('/shifts/series/preview', data),
  getSeries: (seriesId) => api.get(`/shifts/series/${seriesId}`),
  deleteSeries: (seriesId, onlyFuture = false) =>
    api.delete(`/shifts/series/${seriesId}`, { params: { only_future: onlyFuture } }),
  reassignSeries: (seriesId, newEmployeeId, onlyFuture = true) =>
    api.put(`/shifts/series/${seriesId}/reassign`, {
      new_employee_id: newEmployeeId,
      only_future: onlyFuture,
    }),
  update: (id, data) => api.put(`/shifts/${id}`, data),
  delete: (id) => api.delete(`/shifts/${id}`),
  exportPlan: (params) =>
    api.get('/shifts/export-plan', { params, responseType: 'blob' }),
};

// Maßnahmen API
export const personaleinsatzAPI = {
  getAll: (params = {}) => api.get('/personaleinsatz', { params }),
  suggestion: (employeeId) => api.get(`/personaleinsatz/employee-suggestion/${employeeId}`),
  create: (data) => api.post('/personaleinsatz', data),
  update: (id, data) => api.put(`/personaleinsatz/${id}`, data),
  delete: (id) => api.delete(`/personaleinsatz/${id}`),
  export: (params = {}) => api.get('/personaleinsatz/export', { params, responseType: 'blob' }),
};

export const masterMeasuresAPI = {
  getAll: (params = {}) => api.get('/master-measures', { params }),
  getAssignedMassnahmen: (mid) => api.get(`/master-measures/${mid}/assigned-massnahmen`),
  setAssignments: (mid, massnahmeIds) =>
    api.put(`/master-measures/${mid}/assignments`, { massnahme_ids: massnahmeIds }),
  export: (params = {}) => api.get('/master-measures/export', { params, responseType: 'blob' }),
};

// Honorarkräfte (freie Mitarbeit) API
export const honorarfirmenAPI = {
  getAll: () => api.get('/honorarfirmen'),
  create: (data) => api.post('/honorarfirmen', data),
  update: (id, data) => api.put(`/honorarfirmen/${id}`, data),
  delete: (id) => api.delete(`/honorarfirmen/${id}`),
};

export const honorarkraefteAPI = {
  getAll: (params = {}) => api.get('/honorarkraefte', { params }),
  getOne: (id) => api.get(`/honorarkraefte/${id}`),
  create: (data) => api.post('/honorarkraefte', data),
  update: (id, data) => api.put(`/honorarkraefte/${id}`, data),
  delete: (id) => api.delete(`/honorarkraefte/${id}`),
  addContract: (id, data) => api.post(`/honorarkraefte/${id}/contracts`, data),
  updateContract: (id, cid, data) => api.put(`/honorarkraefte/${id}/contracts/${cid}`, data),
  deleteContract: (id, cid) => api.delete(`/honorarkraefte/${id}/contracts/${cid}`),
  auswertung: (year, month, locationId) => api.get('/honorarkraefte/auswertung', { params: { year, month, ...(locationId && locationId !== 'all' ? { location_id: locationId } : {}) } }),
};


export const honorarDocsAPI = {
  getPlaceholders: () => api.get('/honorar-documents/placeholders'),
  listTemplates: (docType) => api.get('/honorar-documents/templates', { params: docType ? { doc_type: docType } : {} }),
  uploadTemplate: (formData) => api.post('/honorar-documents/templates', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteTemplate: (id) => api.delete(`/honorar-documents/templates/${id}`),
  generate: (hid, data) => api.post(`/honorar-documents/honorarkraefte/${hid}/generate`, data),
  generateBundle: (hid, data) => api.post(`/honorar-documents/honorarkraefte/${hid}/generate-bundle`, data),
};


export const massnahmenAPI = {
  getAll: (params = {}) => api.get('/massnahmen', { params }),
  shiftCounts: () => api.get('/massnahmen/shift-counts'),
  // Liefert ALLE aktiven Massnahmen ohne Standort-Scope (für Mitarbeiter-Zuweisung)
  getAllUnscoped: () => api.get('/massnahmen/all'),
  create: (data) => api.post('/massnahmen', data),
  update: (id, data) => api.put(`/massnahmen/${id}`, data),
  delete: (id, { force = false } = {}) => api.delete(`/massnahmen/${id}`, { params: force ? { force: true } : {} }),
  exportTemplate: () => api.get('/massnahmen/export/template', { responseType: 'blob' }),
  reorder: (ids) => api.post('/massnahmen/reorder', { ids }),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/massnahmen/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Teilnehmer-Aktivitäten API
export const activitiesAPI = {
  getAll: (params = {}) => api.get('/activities', { params }),
  create: (data) => api.post('/activities', data),
  createSeries: (data) => api.post('/activities/series', data),
  previewSeries: (data) => api.post('/activities/series/preview', data),
  update: (id, data) => api.put(`/activities/${id}`, data),
  delete: (id) => api.delete(`/activities/${id}`),
};

// Rooms API
export const roomsAPI = {
  getAll: (params = {}) => api.get('/rooms', { params }),
  getOne: (id) => api.get(`/rooms/${id}`),
  create: (data) => api.post('/rooms', data),
  update: (id, data) => api.put(`/rooms/${id}`, data),
  delete: (id) => api.delete(`/rooms/${id}`),
  getBookableByEmployee: () => api.get('/rooms/bookable-by-employee'),
  availability: (params = {}) => api.get('/rooms-availability', { params }),
  exportRooms: (params = {}) =>
    api.get('/rooms/export', { params, responseType: 'blob' }),
  getImportTemplate: (params = {}) =>
    api.get('/rooms/import/template', { params, responseType: 'blob' }),
  importRooms: (file, dryRun = true) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/rooms/import', fd, {
      params: { dry_run: dryRun },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getMyShifts: (startDate, endDate) => api.get('/shifts/my-shifts', { params: { start_date: startDate, end_date: endDate } }),
  getTeamWeekAbsences: () => api.get('/dashboard/team-week-absences'),
  getExpiringContracts: (days = 60) => api.get('/dashboard/expiring-contracts', { params: { days } }),
};

// Feiertage API
export const feiertageAPI = {
  getBundeslaender: () => api.get('/feiertage/bundeslaender'),
  getForYear: (year, bundesland) => api.get(`/feiertage/${year}`, { params: { bundesland } }),
  getFlat: (year) => api.get(`/feiertage/${year}/flat`),
  getByLocation: (locationId, startDate, endDate) =>
    api.get('/holidays-by-location', { params: { location_id: locationId, start_date: startDate, end_date: endDate } }),
  getCompanyFreeDays: (year) => api.get(`/company/free-days/${year}`),
};

// Schulferien API
export const schulferienAPI = {
  getAll: (year, bundesland = null) => api.get('/schulferien', { params: { year, bundesland } }),
  fetch: (year, bundesland = null) => api.post('/schulferien/fetch', null, { params: { year, bundesland } }),
  createManual: (data) => api.post('/schulferien/manual', data),
  delete: (id) => api.delete(`/schulferien/${id}`),
  getBundeslaender: () => api.get('/schulferien/bundeslaender'),
};

// Jobs (Stellenausschreibungen) API
export const jobsAPI = {
  getAll: (status = null) => api.get('/jobs', { params: { status } }),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  updateStatus: (id, status) => api.put(`/jobs/${id}/status`, null, { params: { status } }),
  delete: (id) => api.delete(`/jobs/${id}`),
  getNewCount: () => api.get('/jobs/new-count'),
  markSeen: () => api.post('/jobs/mark-seen'),
};

// News (Neuigkeiten) counter API
export const newsAPI = {
  getNewCount: () => api.get('/news/new-count'),
  markSeen: () => api.post('/news/mark-seen'),
};

// Digitale Signatur (EES)
export const signaturesAPI = {
  signDocument: (docId, data) => api.post(`/signatures/documents/${docId}/sign`, data),
  getAudit: (signedDocId) => api.get(`/signatures/audit/${signedDocId}`),
  createLink: (docId, data = {}) => api.post(`/signatures/documents/${docId}/request-link`, data),
  // Zwei-seitige Signatur (sequentieller Workflow)
  createWorkflow: (data) => api.post('/signatures/workflows', data),
  listWorkflows: (params = {}) => api.get('/signatures/workflows', { params }),
  getWorkflow: (id) => api.get(`/signatures/workflows/${id}`),
  signWorkflowStep: (id, data) => api.post(`/signatures/workflows/${id}/sign`, data),
  remindWorkflow: (id, data = {}) => api.post(`/signatures/workflows/${id}/remind`, data),
  cancelWorkflow: (id) => api.delete(`/signatures/workflows/${id}`),
  // QES (Skribble / eIDAS)
  qesConfig: () => api.get('/signatures/qes/config'),
  createQes: (docId, data) => api.post(`/signatures/documents/${docId}/qes`, data),
  listQes: (params = {}) => api.get('/signatures/qes', { params }),
  getQes: (id) => api.get(`/signatures/qes/${id}`),
  withdrawQes: (id) => api.post(`/signatures/qes/${id}/withdraw`),
};

// People Analytics / Personalanalyse
export const analyticsAPI = {
  getFilters: () => api.get('/analytics/filters'),
  getOverview: (params = {}) => api.get('/analytics/overview', { params }),
};

// Öffentliches Signieren per Link (ohne Login)
export const publicSignAPI = {
  getRequest: (token) => publicApi.get(`/signatures/public/${token}`),
  pdfUrl: (token) => `${API_BASE}/signatures/public/${token}/pdf`,
  sign: (token, data) => publicApi.post(`/signatures/public/${token}/sign`, data),
};

// Recruiting / Bewerbermanagement (intern, authentifiziert)
export const recruitingAPI = {
  getMeta: () => api.get('/recruiting/meta'),
  getStats: () => api.get('/recruiting/stats'),
  listApplications: (params = {}) => api.get('/recruiting/applications', { params }),
  getApplication: (id) => api.get(`/recruiting/applications/${id}`),
  updateApplication: (id, data) => api.patch(`/recruiting/applications/${id}`, data),
  setStatus: (id, status) => api.post(`/recruiting/applications/${id}/status`, { status }),
  addNote: (id, text) => api.post(`/recruiting/applications/${id}/notes`, { text }),
  cvUrl: (id) => `${API_BASE}/recruiting/applications/${id}/cv`,
  deleteApplication: (id) => api.delete(`/recruiting/applications/${id}`),
  convert: (id, data = {}) => api.post(`/recruiting/applications/${id}/convert`, data),
  getPoster: (jobId, baseUrl) => api.get(`/recruiting/jobs/${jobId}/poster`, {
    params: { base_url: baseUrl }, responseType: 'blob',
  }),
  // Empfehlungsprogramm
  getMyReferral: () => api.get('/recruiting/my-referral'),
  getReferralSettings: () => api.get('/recruiting/referral-settings'),
  updateReferralSettings: (data) => api.put('/recruiting/referral-settings', data),
  listReferrals: () => api.get('/recruiting/referrals'),
  markPayout: (appId) => api.post(`/recruiting/referrals/${appId}/payout`),
};

// Öffentliche Karriereseite (ohne Login) – eigener Client ohne Auth-Interceptor
const publicApi = axios.create({ baseURL: API_BASE, timeout: 45000 });
export const careersAPI = {
  listJobs: () => publicApi.get('/careers/jobs'),
  getJob: (id) => publicApi.get(`/careers/jobs/${id}`),
  apply: (jobId, formData) => publicApi.post(`/careers/jobs/${jobId}/apply`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  qrUrl: (jobId, baseUrl) => `${API_BASE}/careers/jobs/${jobId}/qr.png?base_url=${encodeURIComponent(baseUrl)}`,
};

// Phonebook (Telefonbuch) API
export const phonebookAPI = {
  getAll: () => api.get('/phonebook'),
};


// Config API
export const configAPI = {
  getCustomFields: () => api.get('/config/custom-fields'),
  saveCustomFields: (fields) => api.post('/config/custom-fields', fields),
};

// Admin Audit API (HTML response)
export const adminAuditAPI = {
  getHtml: () => api.get('/admin/api-audit', { responseType: 'text', transformResponse: [(d) => d] }),
};

// Room Bookings API
export const roomBookingsAPI = {
  getAll: (params = {}) => api.get('/room-bookings', { params }),
  getOne: (id) => api.get(`/room-bookings/${id}`),
  create: (data) => api.post('/room-bookings', data),
  update: (id, data) => api.put(`/room-bookings/${id}`, data),
  delete: (id) => api.delete(`/room-bookings/${id}`),
};

// Reinigung / Putzplan API
export const cleaningAPI = {
  listSettings: () => api.get('/cleaning/settings'),
  updateSettings: (locationId, data) => api.put(`/cleaning/settings/${locationId}`, data),
  getPlan: (params) => api.get('/cleaning/plan', { params }),
  addManual: (data) => api.post('/cleaning/manual', data),
  removeManual: (roomId, date) => api.delete('/cleaning/manual', { params: { room_id: roomId, date } }),
  excludeAuto: (data) => api.post('/cleaning/exclude', data),
  includeAuto: (roomId, date) => api.delete('/cleaning/exclude', { params: { room_id: roomId, date } }),
  getSummary: (params) => api.get('/cleaning/summary', { params }),
  exportSummary: (params) => api.get('/cleaning/summary/export', { params, responseType: 'blob' }),
};

// E-Mail-Einstellungen (IT-Verwaltung)
export const emailSettingsAPI = {
  get: () => api.get('/email-settings/'),
  update: (data) => api.put('/email-settings/', data),
  sendTest: (toEmail) => api.post('/email-settings/test', { to_email: toEmail }),
};

// Time Tracking API
export const timeTrackingAPI = {
  getStatus: () => api.get('/time-tracking/status'),
  getReminderStatus: () => api.get('/time-tracking/reminder-status'),
  getEntries: (params) => api.get('/time-tracking/entries', { params }),
  getMonthlyEntries: (params) => api.get('/time-tracking/monthly', { params }),
  getBalances: (params) => api.get('/time-tracking/balances', { params }),
  getMyBalanceTrend: (months = 6) => api.get('/time-tracking/my-balance-trend', { params: { months } }),
  clockIn: (data = {}) => api.post('/time-tracking/clock-in', data),
  clockOut: () => api.post('/time-tracking/clock-out'),
  breakStart: () => api.post('/time-tracking/break-start'),
  breakEnd: () => api.post('/time-tracking/break-end'),
  requestCorrection: (data) => api.post('/time-tracking/corrections', data),
  getPlannedShift: (employeeId, date) => api.get('/time-tracking/planned-shift', { params: { employee_id: employeeId, date } }),
  getMyPlannedShift: (date) => api.get('/time-tracking/my-planned-shift', { params: { date } }),
  getNachtragConflicts: (date, employeeIds) => api.post('/time-tracking/nachtrag-conflicts', { date, employee_ids: employeeIds }),
  exportEntries: (params) => api.get('/time-tracking/export', { params, responseType: 'blob' }),
};

// Change Requests API (Änderungsanträge)
export const changeRequestsAPI = {
  create: (data) => api.post('/change-requests', data),
  createBulk: (payload) => api.post('/change-requests/bulk', payload),
  getMyRequests: () => api.get('/change-requests/my'),
  getAll: (params = {}) => api.get('/change-requests', { params }),
  getPendingCount: () => api.get('/change-requests/pending-count'),
  updateStatus: (id, status, rejectionReason = null) => 
    api.put(`/change-requests/${id}/status`, null, { 
      params: { status, rejection_reason: rejectionReason } 
    }),
  delete: (id) => api.delete(`/change-requests/${id}`),
};

// Location Times API (Standort-Zeiten)
export const locationTimesAPI = {
  create: (data) => api.post('/location-times', data),
  getAll: (params = {}) => api.get('/location-times', { params }),
  getToday: () => api.get('/location-times/today'),
  getCategories: () => api.get('/location-times/categories'),
  getAvailableLocations: () => api.get('/location-times/available-locations'),
  delete: (id) => api.delete(`/location-times/${id}`),
};

// Workflow API (On-/Off-Boarding)
export const workflowAPI = {
  // Templates
  getTemplates: (params = {}) => api.get('/workflow-templates', { params }),
  getTemplate: (id) => api.get(`/workflow-templates/${id}`),
  createTemplate: (data) => api.post('/workflow-templates', data),
  updateTemplate: (id, data) => api.put(`/workflow-templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/workflow-templates/${id}`),
  
  // Template Tasks
  addTask: (templateId, task) => api.post(`/workflow-templates/${templateId}/tasks`, task),
  updateTask: (templateId, taskId, task) => api.put(`/workflow-templates/${templateId}/tasks/${taskId}`, task),
  deleteTask: (templateId, taskId) => api.delete(`/workflow-templates/${templateId}/tasks/${taskId}`),
  reorderTasks: (templateId, taskIds) => api.put(`/workflow-templates/${templateId}/tasks/reorder`, { task_ids: taskIds }),
  
  // Workflow Instances
  startWorkflow: (data) => api.post('/workflows/start', data),
  getWorkflows: (params = {}) => api.get('/workflows', { params }),
  getWorkflow: (id) => api.get(`/workflows/${id}`),
  getActiveCount: () => api.get('/workflows/active-count'),
  completeTask: (workflowId, taskId, data = {}) => api.put(`/workflows/${workflowId}/tasks/${taskId}/complete`, data),
  skipTask: (workflowId, taskId, notes = null) => api.put(`/workflows/${workflowId}/tasks/${taskId}/skip`, null, { params: { notes } }),
  cancelWorkflow: (id) => api.put(`/workflows/${id}/cancel`),

  // Auto-Triggers
  getTriggers: () => api.get('/workflow-triggers'),
  updateTrigger: (type, data) => api.put(`/workflow-triggers/${type}`, data),
  checkTriggers: () => api.post('/workflow-triggers/check'),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params = {}) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  getSettings: () => api.get('/notification-settings'),
  updateSettings: (data) => api.put('/notification-settings', data),
};

// Employee Requests API (Mitarbeiter-Anträge)
export const employeeRequestsAPI = {
  getTemplates: (params = {}) => api.get('/requests/templates', { params }),
  createTemplate: (data) => api.post('/requests/templates', data),
  updateTemplate: (id, data) => api.put(`/requests/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/requests/templates/${id}`),
  submit: (data) => api.post('/requests', data),
  approvalPreview: (templateId) => api.get(`/requests/templates/${templateId}/approval-preview`),
  getAll: (params = {}) => api.get('/requests', { params }),
  getPending: () => api.get('/requests/pending'),
  getOne: (id) => api.get(`/requests/${id}`),
  decide: (id, data) => api.post(`/requests/${id}/decide`, data),
  cancel: (id) => api.post(`/requests/${id}/cancel`),
};

// Admin API (Konsistenz-Checks)
export const adminAPI = {
  consistencyCheck: () => api.get('/admin/consistency-check'),
  notificationMatrix: () => api.get('/admin/notification-matrix'),
  autoFixLink: (employeeId) =>
    api.post('/admin/consistency-check/auto-fix-link', null, { params: { employee_id: employeeId } }),
  autoFixLinkAll: () => api.post('/admin/consistency-check/auto-fix-link-all'),
  clearBrokenLink: (employeeId) =>
    api.post('/admin/consistency-check/clear-broken-link', null, { params: { employee_id: employeeId } }),
  assignLocation: (employeeId, locationId) =>
    api.post('/admin/consistency-check/assign-location', null, {
      params: { employee_id: employeeId, location_id: locationId },
    }),
  deleteOrphanUser: (userId) =>
    api.delete('/admin/consistency-check/delete-orphan-user', { params: { user_id: userId } }),
  bulkDeleteOrphanUsers: () =>
    api.post('/admin/consistency-check/bulk-delete-orphan-users'),
  bulkClearBrokenLinks: () =>
    api.post('/admin/consistency-check/bulk-clear-broken-links'),
  timeTrackingOrphans: () => api.get('/admin/time-tracking/orphans'),
  closeOrphanEntry: (id) => api.post(`/admin/time-tracking/orphans/${id}/close`),
  deleteOrphanEntry: (id) => api.delete(`/admin/time-tracking/orphans/${id}`),
  dataHeal: () => api.post('/admin/data-heal'),
  dataHealScan: () => api.get('/admin/data-heal/scan'),
  missingIdsScan: () => api.get('/admin/consistency-check/missing-ids/scan'),
  missingIdsHeal: () => api.post('/admin/consistency-check/missing-ids/heal'),
};

// Vehicles / Fuhrpark API
export const vehiclesAPI = {
  list: (params = {}) => api.get('/vehicles', { params }),
  get: (id) => api.get(`/vehicles/${id}`),
  create: (data) => api.post('/vehicles', data),
  update: (id, data) => api.put(`/vehicles/${id}`, data),
  delete: (id) => api.delete(`/vehicles/${id}`),
  history: (id) => api.get(`/vehicles/${id}/history`),
  addNote: (id, comment) => api.post(`/vehicles/${id}/history/note`, { comment }),
  updateKm: (id, current_km, comment) => api.post(`/vehicles/${id}/km`, { current_km, comment }),
  importFromExcel: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/vehicles/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  exportXlsx: () => api.get('/vehicles/export/xlsx', { responseType: 'blob' }),
  // Bookings
  poolVehicles: (params = {}) => api.get('/vehicle-bookings/pool-vehicles', { params }),
  listBookings: (params = {}) => api.get('/vehicle-bookings', { params }),
  createBooking: (data) => api.post('/vehicle-bookings', data),
  updateBooking: (id, data) => api.put(`/vehicle-bookings/${id}`, data),
  cancelBooking: (id) => api.delete(`/vehicle-bookings/${id}`),
  // Phase 2: Schäden / Wartung / Foto-Upload / Dashboard-Summary
  listEvents: (vehicleId, params = {}) => api.get(`/vehicles/${vehicleId}/events`, { params }),
  createEvent: (vehicleId, data) => api.post(`/vehicles/${vehicleId}/events`, data),
  updateEvent: (vehicleId, eventId, data) => api.put(`/vehicles/${vehicleId}/events/${eventId}`, data),
  deleteEvent: (vehicleId, eventId) => api.delete(`/vehicles/${vehicleId}/events/${eventId}`),
  uploadPhoto: (vehicleId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/vehicles/${vehicleId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  photoUrl: (photoId) => `${BACKEND_URL}/api/vehicles/photos/${photoId}`,
  dashboardSummary: () => api.get('/vehicles/dashboard/summary'),
};

// Haustechnik / Facility-Management (Schadensmeldungen & Aufgaben)
export const facilityAPI = {
  meta: () => api.get('/facility/meta'),
  list: (params = {}) => api.get('/facility/tickets', { params }),
  get: (id) => api.get(`/facility/tickets/${id}`),
  create: (data) => api.post('/facility/tickets', data),
  update: (id, data) => api.patch(`/facility/tickets/${id}`, data),
  remove: (id) => api.delete(`/facility/tickets/${id}`),
  decide: (id, action, note = '') => api.post(`/facility/tickets/${id}/decision`, { action, note }),
  setQuote: (id, amount, note = '') => api.post(`/facility/tickets/${id}/quote`, { amount, note }),
  assign: (id, assigned_to) => api.post(`/facility/tickets/${id}/assign`, { assigned_to }),
  schedule: (id, payload) => api.post(`/facility/tickets/${id}/schedule`, payload),
  setStatus: (id, status) => api.post(`/facility/tickets/${id}/status`, { status }),
  complete: (id, payload) => api.post(`/facility/tickets/${id}/complete`, payload),
  uploadPhoto: (id, file, kind = 'report') => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/facility/tickets/${id}/photos`, fd, {
      params: { kind },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  photoBlob: (photoId) => api.get(`/facility/photos/${photoId}`, { responseType: 'blob' }),
  deletePhoto: (photoId) => api.delete(`/facility/photos/${photoId}`),
  dashboardSummary: () => api.get('/facility/dashboard/summary'),
  updateSettings: (data) => api.patch('/facility/settings', data),
  // Self-Service für normale Mitarbeiter (ohne Board-Zugriff)
  selfMeta: () => api.get('/facility/self/meta'),
  report: (data) => api.post('/facility/report', data),
  myReports: () => api.get('/facility/my-reports'),
  uploadReportPhoto: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/facility/report/${id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  reportPhotoBlob: (photoId) => api.get(`/facility/report/photos/${photoId}`, { responseType: 'blob' }),
};

// Eigenes Profil: Mitarbeiter-eigene IT-Zugänge
export const myProfileAPI = {
  getItAccess: () => api.get('/my-it-access'),
  updateItAccess: (accessId, payload) => api.put(`/my-it-access/${accessId}`, payload),
  getReminderPreferences: () => api.get('/employees/me/reminder-preferences'),
  updateReminderPreferences: (payload) => api.patch('/employees/me/reminder-preferences', payload),
};

// iCal-Export: Token-basierte Kalender-Subscription für eigene Einsätze
export const icalAPI = {
  getMyToken: () => api.get('/users/me/ical-token'),
  regenerateMyToken: () => api.post('/users/me/ical-token/regenerate'),
  getMyCalendarEvents: (startDate, endDate) =>
    api.get('/me/calendar-events', { params: { start_date: startDate, end_date: endDate } }),
};

// Berechtigungen-Selbsttest (Admin-Audit der Rollen-Zuweisungen)
export const permissionsAuditAPI = {
  getAudit: () => api.get('/admin/permissions-audit'),
  heal: () => api.post('/admin/permissions-audit/heal'),
};

// Soll-Stunden Audit-Report (monatliche Lohn-Übergabe-Kontrolle)
export const workingHoursAuditAPI = {
  getAudit: (params) => api.get('/admin/working-hours-audit', { params }),
  exportUrl: (params) => {
    const qs = new URLSearchParams(params).toString();
    return `${API_BASE}/admin/working-hours-audit/export?${qs}`;
  },
};

// Tages-Aufschluesselung Soll/Ist (Heatmap-Datenquelle fuer Zeiterfassung-Web-UI)
export const dailyBreakdownAPI = {
  get: (params) => api.get('/time-tracking/daily-breakdown', { params }),
};

// TV/Terminal-Geraete (Admin-Verwaltung)
export const devicesAPI = {
  list: () => api.get('/devices'),
  create: (data) => api.post('/devices', data),
  rotate: (id) => api.post(`/devices/${id}/rotate`),
  update: (id, params) => api.patch(`/devices/${id}`, null, { params }),
  delete: (id) => api.delete(`/devices/${id}`),
};

// Qualifikations-/Weiterbildungs-Anträge (Self-Service mit Vorgesetzten-Freigabe)
export const qualificationProposalsAPI = {
  /**
   * @param {Object} opts
   * @param {"training"|"qualification"} opts.kind
   * @param {"create"|"update"|"delete"} opts.action
   * @param {string} [opts.target_id]    Für update/delete: ID des bestehenden Eintrags
   * @param {Object} [opts.payload]      Felder (name, provider, date, expiry_date, …)
   * @param {string} [opts.reason]       Begründung (optional, freitext)
   * @param {File}   [opts.file]         Zertifikat / Nachweis-Datei
   */
  create: ({ kind, action, target_id, payload, reason, file }) => {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('action', action);
    if (target_id) fd.append('target_id', target_id);
    fd.append('payload_json', JSON.stringify(payload || {}));
    if (reason) fd.append('reason', reason);
    if (file) fd.append('file', file);
    return api.post('/qualification-proposals', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listMine: () => api.get('/qualification-proposals/mine'),
  withdraw: (id) => api.delete(`/qualification-proposals/${id}`),
  inbox: (status = 'pending') => api.get('/qualification-proposals/inbox', { params: { status } }),
  forEmployee: (employee_id, status = 'pending') =>
    api.get(`/qualification-proposals/employee/${employee_id}`, { params: { status } }),
  edit: (id, body) => api.patch(`/qualification-proposals/${id}/edit`, body),
  approve: (id) => api.post(`/qualification-proposals/${id}/approve`),
  reject: (id, reason) => api.post(`/qualification-proposals/${id}/reject`, { reason }),
};

// QMB / Qualitätsmanagement – eigener Infopunkt (Zielgruppe nach Maßnahme/Abteilung)
export const qmbAPI = {
  meta: () => api.get('/qmb/meta'),
  listPosts: () => api.get('/qmb/posts'),
  dashboardSummary: () => api.get('/qmb/dashboard-summary'),
  createPost: (data) => api.post('/qmb/posts', data),
  updatePost: (id, data) => api.patch(`/qmb/posts/${id}`, data),
  audiencePreview: (data) => api.post('/qmb/audience-preview', data),
  deletePost: (id) => api.delete(`/qmb/posts/${id}`),
  confirmPost: (id) => api.post(`/qmb/posts/${id}/confirm`),
  getConfirmations: (id) => api.get(`/qmb/posts/${id}/confirmations`),
  remind: (id) => api.post(`/qmb/posts/${id}/remind`),
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/qmb/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  fileUrl: (fileId) => `${API_BASE}/qmb/files/${fileId}?token=${getToken()}`,
  exportUrl: (postId) => `${API_BASE}/qmb/posts/${postId}/confirmations/export?token=${getToken()}`,
  // Editors (Admin)
  getEditors: () => api.get('/qmb/editors'),
  setEditors: (userIds) => api.put('/qmb/editors', { user_ids: userIds }),
};


export default api;
